/**
 * Camera-based document capture component with real-time guidance.
 *
 * Opens the device camera (rear-facing on mobile, user-facing fallback
 * on desktop), displays a credit-card aspect ratio overlay with corner
 * alignment guides, and runs client-side image quality checks after
 * capture. Falls back to a file upload button when camera access is
 * unavailable.
 *
 * @module components/DocumentCapture
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImageQuality, type ImageQualityResult } from '../hooks/useImageQuality';
import {
  useDocumentEdgeDetection,
  type EdgeDetectionState,
} from '../hooks/useDocumentEdgeDetection';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DocumentCaptureProps {
  /** Called when a capture passes quality checks (or the user accepts with warnings). */
  onCapture: (blob: Blob, quality: ImageQualityResult) => void;
  /** Called when the user chooses the file-upload fallback. */
  onFallbackUpload?: (file: File) => void;
  /** Human-readable document type for guidance text. */
  documentType?: string;
  /** Called when the user cancels / closes the capture UI. */
  onClose?: () => void;
  /** When true, auto-captures after document is stable for ~1.5s (default false). */
  autoCapture?: boolean;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CapturePhase =
  | 'requesting'   // Waiting for camera permissions
  | 'streaming'    // Camera feed is active
  | 'assessing'    // Quality check in progress
  | 'preview'      // Showing captured image with quality result
  | 'error';       // Camera access denied or unavailable

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Credit-card aspect ratio (ISO/IEC 7810 ID-1). */
const CARD_ASPECT = 85.6 / 53.98; // ~1.586

// ---------------------------------------------------------------------------
// Corner guides SVG overlay
// ---------------------------------------------------------------------------

/**
 * Returns the stroke color for a corner guide based on which borders
 * that corner touches. A corner is green only when BOTH of its adjacent
 * borders are detected; otherwise it uses the default color.
 */
function cornerColor(
  borderA: boolean,
  borderB: boolean,
  allDetected: boolean,
): string {
  if (allDetected) return '#7FB0F2'; // primary-light (detected feedback)
  if (borderA && borderB) return '#7FB0F2';
  return 'white';
}

function CornerGuides({ edgeState }: { edgeState: EdgeDetectionState }) {
  const { borders, isDetected } = edgeState;

  const tl = cornerColor(borders.top, borders.left, isDetected);
  const tr = cornerColor(borders.top, borders.right, isDetected);
  const bl = cornerColor(borders.bottom, borders.left, isDetected);
  const br = cornerColor(borders.bottom, borders.right, isDetected);

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 400 252"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {/* Top-left */}
      <path d="M12 42 L12 12 L42 12" stroke={tl} strokeWidth="3" strokeLinecap="round">
        <animate attributeName="opacity" values="1;0.6;1" dur="1.5s" repeatCount={isDetected ? 'indefinite' : '0'} />
      </path>
      {/* Top-right */}
      <path d="M358 12 L388 12 L388 42" stroke={tr} strokeWidth="3" strokeLinecap="round">
        <animate attributeName="opacity" values="1;0.6;1" dur="1.5s" repeatCount={isDetected ? 'indefinite' : '0'} />
      </path>
      {/* Bottom-left */}
      <path d="M12 210 L12 240 L42 240" stroke={bl} strokeWidth="3" strokeLinecap="round">
        <animate attributeName="opacity" values="1;0.6;1" dur="1.5s" repeatCount={isDetected ? 'indefinite' : '0'} />
      </path>
      {/* Bottom-right */}
      <path d="M358 240 L388 240 L388 210" stroke={br} strokeWidth="3" strokeLinecap="round">
        <animate attributeName="opacity" values="1;0.6;1" dur="1.5s" repeatCount={isDetected ? 'indefinite' : '0'} />
      </path>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Quality score indicator
// ---------------------------------------------------------------------------

function QualityIndicator({ result }: { result: ImageQualityResult }) {
  const { t } = useTranslation('portal');
  const pct = Math.round(result.overallScore * 100);

  let barColor: string;
  let label: string;
  if (result.overallScore >= 0.6) {
    barColor = 'bg-primary';
    label = t('documentCapture.quality.good');
  } else if (result.overallScore >= 0.3) {
    barColor = 'bg-warning';
    label = t('documentCapture.quality.acceptable');
  } else {
    barColor = 'bg-danger';
    label = t('documentCapture.quality.poor');
  }

  return (
    <div className="w-full">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-ink-muted">{label}</span>
        <span className="text-xs text-ink-muted">{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={t('documentCapture.quality.imageQualityAria', { percent: pct })}>
        <div className={`h-full rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Issue list
// ---------------------------------------------------------------------------

function IssueList({ issues }: { issues: ImageQualityResult['issues'] }) {
  if (issues.length === 0) return null;

  return (
    <ul className="mt-3 space-y-1.5">
      {issues.map((issue, i) => (
        <li
          key={`${issue.type}-${i}`}
          className={[
            'flex items-start gap-2 rounded-lg px-3 py-2 text-xs',
            issue.severity === 'rejection'
              ? 'bg-danger-tint text-danger'
              : 'bg-warning-tint text-warning',
          ].join(' ')}
        >
          {issue.severity === 'rejection' ? (
            <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          )}
          <span>{issue.message}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentCapture({
  onCapture,
  onFallbackUpload,
  documentType,
  onClose,
  autoCapture = false,
}: DocumentCaptureProps) {
  const { t } = useTranslation('portal');
  const resolvedDocumentType = documentType ?? t('documentCapture.defaultDocumentType');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fallbackInputRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<CapturePhase>('requesting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [qualityResult, setQualityResult] = useState<ImageQualityResult | null>(null);

  const { assessImage } = useImageQuality();

  // -----------------------------------------------------------------------
  // Edge detection
  // -----------------------------------------------------------------------

  // handleCapture is defined below; we use a ref so the hook's auto-capture
  // callback never triggers a re-creation of the detection loop.
  const handleCaptureRef = useRef<() => void>(() => {});

  const edgeState = useDocumentEdgeDetection({
    videoRef,
    enabled: phase === 'streaming',
    autoCapture,
    onAutoCapture: useCallback(() => {
      handleCaptureRef.current();
    }, []),
  });

  // -----------------------------------------------------------------------
  // Camera lifecycle
  // -----------------------------------------------------------------------

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setPhase('requesting');
    setErrorMessage(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setPhase('streaming');
    } catch (err: unknown) {
      stopStream();
      setPhase('error');

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setErrorMessage(t('documentCapture.errors.accessDenied'));
        } else if (err.name === 'NotFoundError') {
          setErrorMessage(t('documentCapture.errors.notFound'));
        } else {
          setErrorMessage(t('documentCapture.errors.generic', { message: err.message }));
        }
      } else {
        setErrorMessage(t('documentCapture.errors.unavailable'));
      }
    }
  }, [stopStream, t]);

  // Start camera on mount
  useEffect(() => {
    startCamera();
    return () => {
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // -----------------------------------------------------------------------
  // Capture
  // -----------------------------------------------------------------------

  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Draw the current video frame to canvas at native video resolution
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Stop the camera stream while we assess
    stopStream();

    setPhase('assessing');

    // Convert canvas to blob
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92),
    );

    if (!blob) {
      setPhase('error');
      setErrorMessage(t('documentCapture.errors.captureFailed'));
      return;
    }

    // Create a preview URL
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    setCapturedBlob(blob);

    // Run quality assessment
    const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
    const result = await assessImage(file);
    setQualityResult(result);
    setPhase('preview');
  }, [stopStream, assessImage, t]);

  // Keep the auto-capture ref in sync with the latest handleCapture
  handleCaptureRef.current = handleCapture;

  // -----------------------------------------------------------------------
  // Retake / Accept
  // -----------------------------------------------------------------------

  const handleRetake = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCapturedBlob(null);
    setQualityResult(null);
    startCamera();
  }, [previewUrl, startCamera]);

  const handleAccept = useCallback(() => {
    if (capturedBlob && qualityResult) {
      onCapture(capturedBlob, qualityResult);
    }
  }, [capturedBlob, qualityResult, onCapture]);

  // -----------------------------------------------------------------------
  // Fallback file upload
  // -----------------------------------------------------------------------

  const handleFallbackChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && onFallbackUpload) {
        onFallbackUpload(file);
      }
    },
    [onFallbackUpload],
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="w-full rounded-lg border border-hairline bg-white">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
        <h3 className="text-sm font-semibold text-ink">
          {t('documentCapture.scanTitle', { documentType: resolvedDocumentType })}
        </h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]"
            aria-label={t('documentCapture.closeLabel')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Camera / Preview area */}
      <div className="relative bg-ink-surface">
        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

        {/* Live video feed */}
        {(phase === 'requesting' || phase === 'streaming') && (
          <div className="relative aspect-video w-full overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
              aria-label={t('documentCapture.previewLabel', { documentType: resolvedDocumentType })}
            />

            {/* Document frame overlay */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div
                className={[
                  'relative rounded-lg border-2 transition-colors duration-300',
                  edgeState.isDetected
                    ? 'nv-edge-pulse border-solid border-primary-light'
                    : 'border-dashed border-white/60',
                ].join(' ')}
                style={{
                  width: '80%',
                  aspectRatio: `${CARD_ASPECT}`,
                }}
              >
                <CornerGuides edgeState={edgeState} />
              </div>
            </div>

            {/* Guidance text -- aria-live so screen readers announce detection changes */}
            <div className="absolute bottom-0 start-0 end-0 bg-ink/70 px-4 pb-4 pt-8" aria-live="polite" aria-atomic="true">
              {edgeState.isDetected ? (
                <>
                  <p className="text-center text-base font-semibold text-primary-light">
                    {t('documentCapture.documentDetected')}
                  </p>
                  <p className="mt-1 text-center text-sm text-primary-light/80">
                    {autoCapture ? t('documentCapture.holdSteadyAuto') : t('documentCapture.holdSteadyManual')}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-center text-base font-semibold text-white">
                    {t('documentCapture.alignDocument', { documentType: resolvedDocumentType })}
                  </p>
                  <p className="mt-1 text-center text-sm text-white/70">
                    {edgeState.confidence > 0
                      ? t('documentCapture.almostThere')
                      : t('documentCapture.holdSteadyClear')}
                  </p>
                </>
              )}
            </div>

            {/* Loading overlay while requesting permission */}
            {phase === 'requesting' && (
              <div className="absolute inset-0 flex items-center justify-center bg-ink/80">
                <div className="text-center">
                  <div
                    className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"
                    role="status"
                    aria-label={t('documentCapture.requestingAccessLabel')}
                  />
                  <p className="text-sm text-white">{t('documentCapture.requestingAccess')}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assessing overlay */}
        {phase === 'assessing' && (
          <div className="flex aspect-video w-full items-center justify-center bg-ink-surface">
            <div className="text-center">
              <div
                className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"
                role="status"
                aria-label={t('documentCapture.checkingQualityLabel')}
              />
              <p className="text-sm text-white">{t('documentCapture.checkingQuality')}</p>
            </div>
          </div>
        )}

        {/* Preview */}
        {phase === 'preview' && previewUrl && (
          <div className="relative aspect-video w-full overflow-hidden">
            <img
              src={previewUrl}
              alt={t('documentCapture.previewAlt', { documentType: resolvedDocumentType })}
              className="h-full w-full object-contain bg-ink-surface"
            />
          </div>
        )}

        {/* Error state */}
        {phase === 'error' && (
          <div className="flex aspect-video w-full items-center justify-center bg-surface-sunken px-6">
            <div className="text-center">
              <svg
                className="mx-auto mb-3 h-10 w-10 text-ink-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
                />
              </svg>
              <p className="text-sm font-medium text-ink-muted">{t('documentCapture.cameraUnavailable')}</p>
              <p className="mt-1 max-w-xs text-xs text-ink-muted">{errorMessage}</p>
            </div>
          </div>
        )}
      </div>

      {/* Quality feedback + actions */}
      <div className="p-4">
        {/* Quality result when in preview */}
        {phase === 'preview' && qualityResult && (
          <div className="mb-4">
            <QualityIndicator result={qualityResult} />
            <IssueList issues={qualityResult.issues} />
          </div>
        )}

        {/* Capture button */}
        {phase === 'streaming' && (
          <button
            type="button"
            onClick={handleCapture}
            className="flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface"
            style={{
              backgroundColor: 'var(--nv-primary-color)',
              '--tw-ring-color': 'var(--nv-primary-color)',
            } as React.CSSProperties}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
            {t('documentCapture.captureDocument', { documentType: resolvedDocumentType })}
          </button>
        )}

        {/* Assessing state */}
        {phase === 'assessing' && (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-surface-sunken px-4 py-3 text-sm text-ink-muted">
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-hairline-strong border-t-transparent"
              aria-hidden="true"
            />
            {t('documentCapture.analysingImage')}
          </div>
        )}

        {/* Preview actions */}
        {phase === 'preview' && qualityResult && (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleRetake}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-hairline-strong px-4 py-3 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)] focus:ring-offset-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
              {t('documentCapture.retake')}
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!qualityResult.isAcceptable}
              className="flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: 'var(--nv-primary-color)',
                '--tw-ring-color': 'var(--nv-primary-color)',
              } as React.CSSProperties}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {qualityResult.isAcceptable ? t('documentCapture.useThisPhoto') : t('documentCapture.qualityTooLow')}
            </button>
          </div>
        )}

        {/* Non-acceptable quality rejection message */}
        {phase === 'preview' && qualityResult && !qualityResult.isAcceptable && (
          <p className="mt-3 text-center text-xs text-danger" role="alert">
            {t('documentCapture.qualityBelowThreshold')}
          </p>
        )}

        {/* Fallback upload + error retry */}
        {(phase === 'error' || phase === 'streaming') && (
          <div className={phase === 'error' ? '' : 'mt-3'}>
            {phase === 'error' && (
              <button
                type="button"
                onClick={startCamera}
                className="mb-3 w-full rounded-lg border border-hairline-strong px-4 py-3 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)] focus:ring-offset-2"
              >
                {t('documentCapture.tryAgain')}
              </button>
            )}

            {onFallbackUpload && (
              <>
                <button
                  type="button"
                  onClick={() => fallbackInputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong px-4 py-3 text-sm text-ink-muted transition-all duration-200 hover:border-hairline-strong hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)] focus:ring-offset-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-8m0 0l-3 3m3-3l3 3M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5" />
                  </svg>
                  {t('documentCapture.uploadFromFiles')}
                </button>
                <input
                  ref={fallbackInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFallbackChange}
                  className="sr-only"
                  aria-label={t('documentCapture.uploadFromFilesLabel', { documentType: resolvedDocumentType })}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
